<template>
  <div class="rm-screen wm-req">
    <div class="rm-segment" role="group" :aria-label="$t('warehouse_shell.mode')">
      <button
        v-for="mode in modes"
        :key="mode.value"
        type="button"
        class="rm-segment__btn"
        :class="{ 'is-active': stockType === mode.value }"
        :data-mode="mode.value"
        :aria-pressed="String(stockType === mode.value)"
        @click="stockType = mode.value"
      >
        {{ mode.label }}
      </button>
    </div>

    <div class="wm-toggles">
      <button
        type="button"
        class="rm-chip"
        :class="{ 'rm-chip--accent': showStock }"
        data-toggle="stock"
        :aria-pressed="String(showStock)"
        @click="showStock = !showStock"
      >
        <v-icon size="18" :color="showStock ? '#9a3412' : '#1c1917'">mdi-warehouse</v-icon>
        {{ $t("warehouse.show_warehouse_status") }}
      </button>
      <button
        type="button"
        class="rm-chip"
        :class="{ 'rm-chip--accent': showAllGroceries }"
        data-toggle="all"
        :aria-pressed="String(showAllGroceries)"
        @click="showAllGroceries = !showAllGroceries"
      >
        <v-icon size="18" :color="showAllGroceries ? '#9a3412' : '#1c1917'">mdi-food-apple-outline</v-icon>
        {{ $t("warehouse.show_all_groceries") }}
      </button>
    </div>

    <div v-if="rows.length && !busy" class="rm-stats" :class="{ 'rm-stats--pair': kpis.length < 3 }">
      <div v-for="kpi in kpis" :key="kpi.key" class="rm-stat" :class="kpi.tone ? `rm-stat--${kpi.tone}` : ''">
        <span class="rm-stat__label">{{ kpi.label }}</span>
        <span class="rm-stat__value rm-num">
          {{ kpi.value }}<span v-if="kpi.of != null" class="wm-of"> / {{ kpi.of }}</span>
        </span>
      </div>
    </div>

    <p v-if="showStockCombinedHint" class="wm-note">
      <v-icon size="16" color="#5f5852">mdi-information-outline</v-icon>
      {{ $t("warehouse.stock_combined_hint") }}
    </p>

    <div v-if="busy" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>
    <div v-else-if="!rows.length" class="rm-empty">
      <v-icon size="40" color="#8a817a">mdi-clipboard-list-outline</v-icon>
      <b>{{ emptyTitle }}</b>
      <span v-if="emptyText">{{ emptyText }}</span>
    </div>

    <section v-else class="rm-card">
      <ul class="rm-list">
        <li v-for="row in rows" :key="row.key" class="wm-item" :class="{ 'is-picked': row.picked }">
          <div class="wm-item__top">
            <span class="wm-thumb">
              <img v-if="row.image" :src="row.image" alt="" loading="lazy" />
              <v-icon v-else size="18" color="#8a817a">mdi-food-apple-outline</v-icon>
            </span>
            <span class="wm-item__text">
              <span class="wm-item__name">{{ row.name }}</span>
              <span class="rm-muted rm-num">{{ row.code || "—" }}</span>
            </span>
            <span class="wm-item__qty rm-num">
              <b>{{ qty(row.quantity) }}</b>
              <span class="wm-unit">{{ row.measure }}</span>
            </span>
          </div>

          <div v-if="shiftColumns.length" class="wm-item__shifts">
            <span v-for="shift in shiftColumns" :key="shift" class="wm-shift rm-num" :class="{ 'is-zero': !row[`s${shift}`] }">
              <report-shift-tag :shift="shift"></report-shift-tag>
              {{ qty(row[`s${shift}`]) }}
            </span>
          </div>

          <div v-if="showStock" class="wm-item__stock">
            <span class="rm-muted">{{ $t("warehouse.warehouse_status") }}</span>
            <span class="wm-pill rm-num" :class="row.short ? 'is-low' : 'is-ok'">
              <v-icon size="14">{{ row.short ? "mdi-alert-outline" : "mdi-check" }}</v-icon>
              {{ qty(row.stock) }} {{ row.measure }}
              <span class="rm-visually-hidden">{{ row.short ? $t("warehouse_shell.stock_low") : $t("warehouse_shell.stock_ok") }}</span>
            </span>
          </div>

          <div v-if="actionMode" class="wm-item__order">
            <label class="wm-order-field">
              <span class="rm-muted">{{ $t("warehouse.quantity_for_action", { action: actionVerb }) }}</span>
              <input
                class="wm-qty"
                type="number"
                min="0"
                step="0.001"
                inputmode="decimal"
                :max="stockType === 'exit' ? row.stock : undefined"
                :value="row.order == null ? '' : row.order"
                @input="updateItemOrder(row.item, $event.target.value)"
              />
            </label>
            <span v-if="row.picked" class="wm-after rm-num">
              <span class="rm-muted">
                {{ $t(stockType === "exit" ? "warehouse.quantity_after_issue" : "warehouse.quantity_after_order") }}
              </span>
              <span class="wm-pill" :class="row.afterLow ? 'is-low' : 'is-ok'">
                <v-icon size="14">{{ row.afterLow ? "mdi-alert-outline" : "mdi-check" }}</v-icon>
                {{ qty(row.after) }} {{ row.measure }}
              </span>
            </span>
            <span v-else-if="row.ordered" class="wm-after rm-num rm-muted">
              {{ $t(stockType === "exit" ? "warehouse.exit" : "warehouse.ordered") }}: {{ qty(row.ordered) }} {{ row.measure }}
            </span>
          </div>
        </li>
      </ul>
    </section>

    <div v-if="actionMode && rows.length" class="wm-bar">
      <button
        type="button"
        class="rm-btn rm-btn--primary"
        :class="{ 'is-off': !selectedItems.length }"
        data-action="submit"
        :disabled="!selectedItems.length"
        @click="onAction"
      >
        <v-icon size="20" color="#ffffff">{{ stockType === "exit" ? "mdi-truck-delivery-outline" : "mdi-cart-outline" }}</v-icon>
        {{ $t(stockType === "exit" ? "warehouse.release" : "order") }}<span v-if="selectedItems.length" class="rm-num"> ({{ selectedItems.length }})</span>
      </button>
    </div>

    <ui-sheet v-model="orderSheet" :label="$t('order')">
      <ul class="rm-list rm-actions">
        <li>
          <button type="button" class="rm-actions__row" data-action="request" @click="orderFromSheet(false)">
            <v-icon size="22" color="#1c1917">mdi-file-document-outline</v-icon>
            {{ $t("warehouse.request_quote") }}
          </button>
        </li>
        <li>
          <button type="button" class="rm-actions__row" data-action="purchase" @click="orderFromSheet(true)">
            <v-icon size="22" color="#1c1917">mdi-cart-arrow-down</v-icon>
            {{ $t("warehouse.purchase_order") }}
          </button>
        </li>
      </ul>
    </ui-sheet>

    <warehouse-purchase-order-dialog v-model="purchaseOrderDialog" :edited-item="editedItem" @success="load" />
    <warehouse-waybill-dialog v-model="waybillDialog" :edited-item="editedItem" />
  </div>
</template>

<script>
import "../warehouseMobile.scss";
import WarehouseDesktopRequirement from "../../screens/WarehouseDesktopRequirement.vue";

/**
 * Trebovanje na telefonu: namirnice kao kartice (šifra, količina, smene, stanje, unos količine) sa glavnom radnjom
 * (Naruči / Izdaj) u donjoj traci. Nasleđuje sve iz računarskog ekrana (učitavanje, izračun, štampa, Excel, dijalozi);
 * menja se samo prikaz. Magacini, period, smene, prikaz i pretragu čita iz zaglavlja i filtera ljuske.
 */
export default {
  name: "WarehouseMobileRequirement",
  extends: WarehouseDesktopRequirement,
  data: () => ({
    orderSheet: false,
  }),
  methods: {
    /** Naručivanje: izbor dokumenta u donjoj kartici; izdavanje: odmah otpremnica. */
    onAction() {
      if (this.stockType === "exit") {
        this.openWayBill();
      } else if (this.selectedItems.length) {
        this.orderSheet = true;
      } else {
        this.orderGroceries(false);
      }
    },
    orderFromSheet(isPurchaseOrder) {
      this.orderSheet = false;
      this.orderGroceries(isPurchaseOrder);
    },
  },
};
</script>
